/**
 * Security — the signed-in user's own two-factor settings.
 *
 * WHY THIS PAGE EXISTS
 * The API has supported authenticator-app (TOTP) sign-in since the beginning,
 * but nothing in the interface could reach `/auth/totp/begin`, so every account
 * was stuck on emailed codes. On a deployment where mail is written to disk
 * rather than sent, that means a person cannot sign in without shell access to
 * the server — which is no way to run a demo, let alone a product.
 *
 * SECURITY NOTES
 *  - The enrolment secret is shown once, on this page, and goes nowhere except
 *    back to the API it came from. The QR code is rendered **locally**, in this
 *    browser, by the bundled `qrcode` library. It is never sent to an external
 *    QR service: the otpauth URI *contains the shared secret*, so handing it to
 *    a third party would be handing over the second factor itself.
 *  - Backup codes are displayed exactly once. The server stores only hashes, so
 *    if they are lost the only route back is an administrator reset.
 *  - Enrolment is not finished until a generated code is verified. That proves
 *    the authenticator is actually working before the account starts depending
 *    on it — otherwise a mis-scanned QR would lock the person out at next login.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Card, Button, Spinner, ErrorNotice } from '../components/ui';
import { IconShield } from '../components/icons';

interface BeginResponse {
  /** Base32 secret, for people who cannot scan a QR code. */
  secret: string;
  /** otpauth://totp/... — everything an authenticator needs. */
  uri: string;
}

type Stage = 'idle' | 'scanning' | 'done';

export function SecurityPage() {
  const { t } = useTranslation();
  const { session, setSession } = useAuth();
  const toMessage = useApiError();

  const [stage, setStage] = useState<Stage>('idle');
  const [enrolment, setEnrolment] = useState<BeginResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const usingTotp = session?.user.mfaMethod === 'TOTP';

  // Render the QR whenever we have a URI. Done in an effect rather than inline
  // because toDataURL is async, and drawing must not block the render pass.
  useEffect(() => {
    if (!enrolment) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(enrolment.uri, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 240,
      // Fixed black-on-white: a themed QR risks failing to scan, and some
      // readers cope badly with low contrast or inverted codes.
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        // Not fatal — the manual secret below is a complete fallback.
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enrolment]);

  useEffect(() => {
    if (stage === 'scanning') codeInputRef.current?.focus();
  }, [stage]);

  async function begin() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<BeginResponse>('/auth/totp/begin', {});
      setEnrolment(res);
      setStage('scanning');
      setCode('');
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ backupCodes: string[] }>('/auth/totp/confirm', {
        code: code.trim(),
      });
      setBackupCodes(res.backupCodes);
      setStage('done');
      setEnrolment(null);
      // The session carries mfaMethod, so update it in place or the page keeps
      // claiming the account is still on emailed codes. There is no re-fetch
      // for this — the value is known, and a silent refresh would cost a
      // round trip to learn something we just caused.
      if (session) {
        setSession({ ...session, user: { ...session.user, mfaMethod: 'TOTP' } });
      }
    } catch (err) {
      setError(toMessage(err));
      setCode('');
      codeInputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  function downloadBackupCodes() {
    if (!backupCodes) return;
    const body = [
      'OpenComply backup codes',
      `Account: ${session?.user.email ?? ''}`,
      `Generated: ${new Date().toISOString()}`,
      '',
      'Each code works once. Keep them somewhere safe and offline.',
      '',
      ...backupCodes.map((c) => `  ${c}`),
      '',
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = 'opencomply-backup-codes.txt';
      a.click();
    } finally {
      // Deferred: Safari resolves the blob asynchronously after the click.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('security.title')}</h1>
        <p className="mt-1 text-muted">{t('security.subtitle')}</p>
      </header>

      <Card>
        <div className="flex items-start gap-4">
          <span className="shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-brand/[0.08] text-brand">
            <IconShield className="w-6 h-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-medium">{t('security.twoFactor')}</h2>
            <p className="mt-1 text-sm text-muted">
              {usingTotp ? t('security.statusTotp') : t('security.statusEmail')}
            </p>

            {!usingTotp && (
              <p className="mt-3 rounded-xl border border-brand/30 bg-brand/[0.06] px-4 py-3 text-sm">
                {t('security.emailWarning')}
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNotice message={error} />
          </div>
        )}

        {/* --- Step 1: offer enrolment ------------------------------------ */}
        {stage === 'idle' && (
          <div className="mt-5 flex flex-wrap gap-3">
            <Button variant="primary" onClick={begin} disabled={busy}>
              {busy && <Spinner className="w-4 h-4" />}
              {usingTotp ? t('security.replaceAuthenticator') : t('security.setUpAuthenticator')}
            </Button>
          </div>
        )}

        {/* --- Step 2: scan and verify ------------------------------------ */}
        {stage === 'scanning' && enrolment && (
          <div className="mt-6 space-y-5">
            <ol className="space-y-5">
              <li>
                <p className="text-sm font-medium">{t('security.step1')}</p>
                <p className="mt-1 text-sm text-muted">{t('security.step1Hint')}</p>
              </li>
              <li>
                <p className="text-sm font-medium">{t('security.step2')}</p>
                <div className="mt-3 flex flex-col sm:flex-row gap-5 items-start">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt={t('security.qrAlt')}
                      width={240}
                      height={240}
                      className="rounded-xl border border-subtle bg-white p-2 shrink-0"
                    />
                  ) : (
                    <div className="w-[240px] h-[240px] grid place-items-center rounded-xl border border-subtle text-sm text-muted shrink-0">
                      {t('security.qrUnavailable')}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted">{t('security.cannotScan')}</p>
                    <button
                      type="button"
                      onClick={() => setShowSecret((s) => !s)}
                      className="mt-2 text-sm text-brand hover:underline"
                    >
                      {showSecret ? t('security.hideKey') : t('security.showKey')}
                    </button>
                    {showSecret && (
                      <code className="mt-2 block break-all rounded-lg bg-surface-sunken px-3 py-2 font-mono text-sm tracking-wider">
                        {enrolment.secret}
                      </code>
                    )}
                  </div>
                </div>
              </li>
              <li>
                <p className="text-sm font-medium">{t('security.step3')}</p>
                <form onSubmit={confirm} className="mt-2 flex flex-wrap items-start gap-3">
                  <input
                    ref={codeInputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    aria-label={t('security.codeLabel')}
                    className="oc-input font-mono !w-36 text-lg tracking-[0.3em]"
                  />
                  <Button type="submit" variant="primary" disabled={busy || code.length !== 6}>
                    {busy && <Spinner className="w-4 h-4" />}
                    {t('security.verify')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setStage('idle');
                      setEnrolment(null);
                      setError(null);
                    }}
                    disabled={busy}
                  >
                    {t('common.cancel')}
                  </Button>
                </form>
              </li>
            </ol>
          </div>
        )}

        {/* --- Step 3: backup codes, shown exactly once -------------------- */}
        {stage === 'done' && backupCodes && (
          <div className="mt-6">
            <p className="rounded-xl border border-good/30 bg-good/[0.06] px-4 py-3 text-sm">
              {t('security.enrolled')}
            </p>

            <h3 className="mt-5 font-medium">{t('security.backupTitle')}</h3>
            <p className="mt-1 text-sm text-muted">{t('security.backupHint')}</p>

            <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {backupCodes.map((c) => (
                <li
                  key={c}
                  className="rounded-lg bg-surface-sunken px-3 py-2 font-mono text-sm text-center tracking-wider"
                >
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-4 flex flex-wrap gap-3">
              <Button variant="primary" onClick={downloadBackupCodes}>{t('security.downloadCodes')}</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setBackupCodes(null);
                  setStage('idle');
                }}
              >
                {t('security.savedThem')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
